import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGetCurrentUser } from '@front/auth/src/authentication-context';

import { isCleaningReference, isWellnessReference } from '@front/common/src/utils/universes';
import { getUniverseRef } from '@front/common/src/ducks/customer/basket-selectors';
import { CommonModalProps } from '@front/common/src/types/components';
import { setBasketAttributeValue } from '@front/common/src/ducks/common/basket';
import { getBasketMode, isSpotCleaningSelector } from '@front/common/src/ducks/customer/basket';

import { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';
import useHasOrWantsMembership from '@front/customer/src/hooks/membership/use-has-or-wants-membership';

import { trackMembershipEvent } from '../../utils/tracking';
import { useAppSelector } from '../../store';

import WellnessMembershipPaywall from './wellness/wellness-membership-paywall';
import CleaningMembershipPaywall from './cleaning/cleaning-membership-paywall';

export type MembershipPaywallProps = CommonModalProps & {
  isClosable?: boolean;
  hasNoInitialOptInChoiceSelection?: boolean;
};

const MembershipPaywall = ({
  isOpen,
  onClose,
  isClosable = true,
  hasNoInitialOptInChoiceSelection,
}: MembershipPaywallProps) => {
  const dispatch = useDispatch();

  const universeReference = useAppSelector(getUniverseRef);
  const user = useGetCurrentUser();
  const mode = useAppSelector(getBasketMode);
  const hasOrWantsMembership = useHasOrWantsMembership();
  const isSpotCleaning = useAppSelector(isSpotCleaningSelector);

  const [optInChoice, setOptInChoice] = useState<MembershipOptIn | undefined>(
    hasNoInitialOptInChoiceSelection ? undefined : 'noMembership',
  );

  const onOptInChoiceSelection = useCallback(
    (newOptIn: MembershipOptIn) => {
      trackMembershipEvent({
        eventType: newOptIn === 'membership' ? 'MemberPriceClick' : 'StandardPriceClick',
        user,
        universeReference,
        mode,
        path: 'MembershipPaywall',
      });

      setOptInChoice(newOptIn);
    },
    [user, universeReference, mode],
  );

  useEffect(() => {
    // To synchronise properly when paywall is opened again, but flag in redux changed
    // Also to track initial selection
    if (isOpen && (isWellnessReference(universeReference) || isSpotCleaning) && !hasNoInitialOptInChoiceSelection) {
      onOptInChoiceSelection(hasOrWantsMembership ? 'membership' : 'noMembership');
    }
  }, [
    isOpen,
    hasOrWantsMembership,
    onOptInChoiceSelection,
    universeReference,
    isSpotCleaning,
    hasNoInitialOptInChoiceSelection,
  ]);

  const closePaywall = useCallback(() => {
    trackMembershipEvent({
      eventType: 'ClosePaywallButtonClick',
      user,
      universeReference,
      mode,
      path: 'MembershipPaywall',
    });

    onClose();
  }, [onClose, universeReference, user, mode]);

  const onConfirmPaywall = useCallback(() => {
    trackMembershipEvent({
      eventType: 'ConfirmButtonClick',
      user,
      universeReference,
      mode,
      path: 'MembershipPaywall',
    });

    dispatch(setBasketAttributeValue('isMembershipRequested', optInChoice === 'membership'));
    onClose();
  }, [user, universeReference, dispatch, onClose, mode, optInChoice]);

  if (isWellnessReference(universeReference)) {
    return (
      <WellnessMembershipPaywall
        isOpen={isOpen}
        closePaywall={isClosable ? closePaywall : undefined}
        onConfirmPaywall={onConfirmPaywall}
        optInChoice={optInChoice}
        onOptInChoiceSelection={onOptInChoiceSelection}
        universeReference={universeReference}
        user={user}
      />
    );
  }

  if (isCleaningReference(universeReference)) {
    return (
      <CleaningMembershipPaywall
        isOpen={isOpen}
        closePaywall={closePaywall}
        onConfirmPaywall={onConfirmPaywall}
        optInChoice={optInChoice}
        onOptInChoiceSelection={onOptInChoiceSelection}
        user={user}
      />
    );
  }

  return null;
};

export default MembershipPaywall;
